import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { useMfa } from "@/hooks/useMfa";
import { supabase } from "@/integrations/supabase/client";

/**
 * Global banner shown to vets/admins whose account still needs an MFA action:
 *  - "enroll": no verified factor yet (must set one up in Settings)
 *  - "verify": factor exists but session is aal1 (must complete /mfa challenge)
 * Hidden on auth/MFA pages to avoid noise where the user is already resolving it.
 */
const HIDDEN_PREFIXES = ["/mfa", "/login", "/signup", "/auth/callback", "/forgot-password", "/reset-password"];

const MfaStatusBanner = () => {
  const { user, loading: authLoading } = useAuth();
  const { role, loading: roleLoading } = useUserRole();
  const mfa = useMfa();
  const location = useLocation();
  const [required, setRequired] = useState<boolean | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user || authLoading || roleLoading) return;
    if (role !== "vet" && role !== "admin") {
      setRequired(false);
      return;
    }
    (async () => {
      const { data } = await supabase.rpc("mfa_required_for", { _user_id: user.id });
      if (!cancelled) setRequired(data !== false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, role, authLoading, roleLoading]);

  if (!user || authLoading || roleLoading || mfa.loading) return null;
  if (role !== "vet" && role !== "admin") return null;
  if (required !== true) return null;
  if (dismissed) return null;
  if (HIDDEN_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"))) {
    return null;
  }

  const aal2 = mfa.currentLevel === "aal2";
  if (aal2 && mfa.enrolled) return null;

  const mode: "enroll" | "verify" = mfa.enrolled ? "verify" : "enroll";

  const config = mode === "verify"
    ? {
        title: "Two-factor verification required",
        body: "Your account has two-factor auth enabled but this session isn't verified yet. Verify now to access your dashboard and admin tools.",
        cta: "Verify now",
        href: "/mfa",
        Icon: ShieldAlert,
      }
    : {
        title: "Set up two-factor authentication",
        body: "As a vet/admin you must enrol an authenticator app to keep your account and patient data secure.",
        cta: "Set up MFA",
        href: "/settings?mfa=required",
        Icon: ShieldCheck,
      };

  const { Icon } = config;

  return (
    <div
      role="status"
      aria-live="polite"
      className="w-full bg-amber-50 border-b border-amber-200 text-amber-900 px-4 py-2.5"
    >
      <div className="max-w-7xl mx-auto flex flex-wrap items-center gap-3 text-sm">
        <Icon className="w-4 h-4 shrink-0" aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <span className="font-semibold">{config.title}.</span>{" "}
          <span className="text-amber-800">{config.body}</span>
        </div>
        <Link
          to={config.href}
          className="inline-flex items-center rounded-full bg-amber-900 text-amber-50 px-3 py-1 text-xs font-medium hover:bg-amber-800 transition-colors"
        >
          {config.cta}
        </Link>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-xs text-amber-800 hover:underline"
          aria-label="Dismiss MFA banner"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
};

export default MfaStatusBanner;
